<template>
  <section class="chart-card">
    <h3 class="card-title">
      业务办理统计
    </h3>
    <div
      ref="chartRef"
      class="chart-box"
      :class="`is-${chartMode}`"
    />
  </section>
</template>

<script setup>
import { markRaw, nextTick, onBeforeUnmount, onMounted, shallowRef, useTemplateRef } from 'vue'
import * as echarts from 'echarts'
import { getBusinessHandlingStatistics } from '@/api/normalChannelStatistics'

const SERIES_ORIGINAL = '原创数字备案'
const SERIES_PRODUCT = '产品发布存证'
const DEFAULT_ITEMS = [
  { name: SERIES_ORIGINAL, value: 0, percentage: '0.00%', currentMonthTotal: 0 },
  { name: SERIES_PRODUCT, value: 0, percentage: '0.00%', currentMonthTotal: 0 }
]

const chartRef = useTemplateRef('chartRef')
const chartMode = shallowRef('desktop')
let chart = null
let resizeObserver = null
let resizeFrame = 0
let itemsCache = DEFAULT_ITEMS
let totalCache = 0

function resolveChartMode() {
  const width = chartRef.value?.clientWidth || window.innerWidth || 0
  if (width <= 520 || window.innerWidth <= 768) return 'mobile'
  if (width <= 900 || window.innerWidth <= 1024) return 'compact'
  return 'desktop'
}

function buildPieLayout(mode) {
  if (mode === 'mobile') {
    return {
      center: ['50%', '38%'],
      radius: ['28%', '46%'],
      legend: {
        orient: 'vertical',
        left: 'center',
        bottom: 10,
        itemGap: 16,
        textStyle: {
          rich: {
            name: { width: 108, color: '#333', fontSize: 13, lineHeight: 20 },
            value: { width: 36, color: '#333', fontSize: 13, fontWeight: 'bold', align: 'right', lineHeight: 20 },
            percent: { width: 62, padding: [0, 0, 0, 8], color: '#999', fontSize: 13, align: 'right', lineHeight: 20 },
            monthly: {
              width: 206,
              padding: [4, 0, 0, 0],
              color: '#999',
              fontSize: 12,
              lineHeight: 18,
              align: 'left'
            }
          }
        }
      },
      graphicLeft: '50%',
      graphicTop: '38%',
      valueFontSize: 26,
      valueY: -10,
      nameY: 16
    }
  }
  return {
    center: ['50%', '52%'],
    radius: mode === 'compact' ? [52, 82] : [58, 92],
    legend: {},
    graphicLeft: '50%',
    graphicTop: '52%',
    valueFontSize: mode === 'compact' ? 22 : 24,
    valueY: -12,
    nameY: mode === 'compact' ? 16 : 18
  }
}

function buildCenterGraphic(total, layout) {
  return [{
    id: 'businessPieCenter',
    type: 'group',
    left: layout.graphicLeft,
    top: layout.graphicTop,
    bounding: 'raw',
    children: [
      {
        type: 'text',
        y: layout.valueY,
        style: {
          text: String(total),
          fill: '#333',
          fontSize: layout.valueFontSize,
          fontWeight: 'bold',
          textAlign: 'center',
          textVerticalAlign: 'middle'
        }
      },
      {
        type: 'text',
        y: layout.nameY,
        style: {
          text: '办理业务总数',
          fill: '#999',
          fontSize: 12,
          textAlign: 'center',
          textVerticalAlign: 'middle'
        }
      }
    ]
  }]
}

function buildDetailGraphics(list, layout) {
  if (!chart || chartMode.value === 'mobile') return []
  const width = chart.getWidth()
  const height = chart.getHeight()
  const centerX = width * 0.5
  const centerY = height * 0.52
  const outerRadius = Array.isArray(layout.radius)
    ? Number(layout.radius[1])
    : Math.min(width, height) * 0.31
  const original = list.find(item => item.name === SERIES_ORIGINAL) || DEFAULT_ITEMS[0]
  const product = list.find(item => item.name === SERIES_PRODUCT) || DEFAULT_ITEMS[1]
  const leftY = centerY - outerRadius * 0.6
  const rightY = centerY + outerRadius * 0.32
  const leftLineEndX = width * 0.34
  const rightLineEndX = width * 0.68
  const buildTextGroup = (id, left, top, title, currentMonthTotal) => ({
    id,
    type: 'group',
    left,
    top,
    bounding: 'raw',
    children: [
      {
        type: 'text',
        style: {
          text: title,
          fill: '#333',
          font: '500 13px "Microsoft YaHei", sans-serif',
          textVerticalAlign: 'middle'
        }
      },
      {
        type: 'text',
        y: 23,
        style: {
          text: `本月新增办理 / ${currentMonthTotal || 0} 件`,
          fill: '#999',
          font: '12px "Microsoft YaHei", sans-serif',
          textVerticalAlign: 'middle'
        }
      }
    ]
  })
  return [
    {
      id: 'businessLeftLine',
      type: 'polyline',
      shape: {
        points: [
          [centerX - outerRadius * 0.72, centerY - outerRadius * 0.7],
          [centerX - outerRadius - 24, leftY],
          [leftLineEndX, leftY]
        ]
      },
      style: { stroke: '#B45A30', lineWidth: 1.5, fill: null }
    },
    {
      id: 'businessLeftDot',
      type: 'circle',
      shape: { cx: leftLineEndX, cy: leftY, r: 4 },
      style: { fill: '#B45A30' }
    },
    buildTextGroup(
      'businessLeftText',
      '13%',
      leftY - 11,
      `办理产品发布存证总数 / ${product.value || 0} 件`,
      product.currentMonthTotal
    ),
    {
      id: 'businessRightLine',
      type: 'polyline',
      shape: {
        points: [
          [centerX + outerRadius * 0.72, centerY + outerRadius * 0.7],
          [centerX + outerRadius + 24, rightY],
          [rightLineEndX, rightY]
        ]
      },
      style: { stroke: '#FFB21D', lineWidth: 1.5, fill: null }
    },
    {
      id: 'businessRightDot',
      type: 'circle',
      shape: { cx: rightLineEndX, cy: rightY, r: 4 },
      style: { fill: '#FFB21D' }
    },
    buildTextGroup(
      'businessRightText',
      '70%',
      rightY - 11,
      `办理原创作品备案总数 / ${original.value || 0} 件`,
      original.currentMonthTotal
    )
  ]
}

function renderChart() {
  if (!chart) return
  chartMode.value = resolveChartMode()
  const mode = chartMode.value
  const layout = buildPieLayout(mode)
  const list = itemsCache.length ? itemsCache : DEFAULT_ITEMS
  const colorMap = {
    [SERIES_ORIGINAL]: new echarts.graphic.LinearGradient(0, 0, 1, 1, [
      { offset: 0, color: '#FFB91F' },
      { offset: 1, color: '#FFE39B' }
    ]),
    [SERIES_PRODUCT]: new echarts.graphic.LinearGradient(0, 0, 1, 1, [
      { offset: 0, color: '#D9B19D' },
      { offset: 1, color: '#A74E24' }
    ])
  }
  const graphics = buildCenterGraphic(totalCache, layout)
  graphics.push(...buildDetailGraphics(list, layout))
  chart.setOption({
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: '{b}<br/>{c}（{d}%）'
    },
    graphic: graphics,
    legend: {
      ...layout.legend,
      show: mode === 'mobile',
      icon: 'circle',
      itemWidth: 10,
      itemHeight: 10,
      data: list.map(item => item.name),
      formatter: (name) => {
        const item = list.find(current => current.name === name) || DEFAULT_ITEMS[0]
        return [
          `{name|${name}}{value|${item.value}}{percent|${item.percentage}}`,
          `{monthly|本月新增办理 ${item.currentMonthTotal || 0} 件}`
        ].join('\n')
      }
    },
    series: [{
      name: '业务办理统计',
      type: 'pie',
      center: layout.center,
      radius: layout.radius,
      startAngle: 90,
      clockwise: true,
      minAngle: 0,
      stillShowZeroSum: false,
      label: { show: false },
      labelLine: { show: false },
      data: list.map(item => ({
        name: item.name,
        value: item.value,
        itemStyle: { color: colorMap[item.name] }
      }))
    }]
  }, { replaceMerge: ['graphic', 'legend', 'series'] })
}

async function loadStatistics() {
  try {
    const response = await getBusinessHandlingStatistics()
    const payload = response && response.code === 200 ? (response.data || {}) : {}
    totalCache = Number(payload.total) || 0
    itemsCache = (payload.items || []).map(item => ({
      name: item.name,
      value: Number(item.value) || 0,
      percentage: item.percentage || '0.00%',
      currentMonthTotal: Number(item.currentMonthTotal) || 0
    }))
  } catch (error) {
    console.error('查询业务办理统计失败:', error)
    totalCache = 0
    itemsCache = DEFAULT_ITEMS
  }
  renderChart()
}

onMounted(async () => {
  await nextTick()
  if (!chartRef.value) return
  chart = markRaw(echarts.init(chartRef.value))
  renderChart()
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame)
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0
        chart?.resize()
        renderChart()
      })
    })
    resizeObserver.observe(chartRef.value)
  }
  loadStatistics()
})

onBeforeUnmount(() => {
  if (resizeFrame) cancelAnimationFrame(resizeFrame)
  resizeObserver?.disconnect()
  chart?.dispose()
})
</script>

<style lang="scss" scoped>
.chart-card {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 20px 24px 16px;
  border: 1px solid #ebeef5;
  border-radius: 2px;
  background: #fff;
}

.card-title {
  margin: 0 0 8px;
  color: #333;
  font-size: 18px;
  font-weight: bold;
}

.chart-box {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: 260px;

  &.is-compact {
    height: 280px;
  }

  &.is-mobile {
    height: 390px;
  }
}

@media (max-width: 768px) {
  .chart-card {
    padding: 16px;
  }

  .card-title {
    margin-bottom: 16px;
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .chart-card {
    padding: 12px;
  }

  .card-title {
    margin-bottom: 12px;
    font-size: 15px;
  }

  .chart-box.is-mobile {
    height: 380px;
  }
}
</style>
